import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, CalendarIcon, ClockIcon, PenToolIcon, MapPinIcon } from 'lucide-react';
interface ScheduleSigningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    date: string;
    time: string;
    location: string;
    notes: string;
  }) => void;
  viewing: any;
}
export function ScheduleSigningModal({
  isOpen,
  onClose,
  onSubmit,
  viewing
}: ScheduleSigningModalProps) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [location, setLocation] = useState('La birou');
  const [notes, setNotes] = useState('');
  const handleSubmit = () => {
    if (!date || !time) {
      alert('Te rog completează data și ora semnării');
      return;
    }
    onSubmit({
      date,
      time,
      location,
      notes
    });
    // Reset
    setDate('');
    setTime('');
    setLocation('La birou');
    setNotes('');
  };
  return <AnimatePresence data-id="element-5477">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-5478" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-5479">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" data-id="element-5480">
              {/* Header */}
              <div className="bg-gradient-to-r from-emerald-500 to-green-600 text-white px-6 py-5" data-id="element-5481">
                <div className="flex items-center justify-between" data-id="element-5482">
                  <div className="flex items-center space-x-3" data-id="element-5483">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm" data-id="element-5484">
                      <PenToolIcon className="w-6 h-6" data-id="element-5485" />
                    </div>
                    <div data-id="element-5486">
                      <h3 className="text-xl font-bold" data-id="element-5487">Programare Semnare</h3>
                      <p className="text-sm text-emerald-100" data-id="element-5488">
                        {viewing?.clientName} - {viewing?.propertyId}
                      </p>
                    </div>
                  </div>
                  <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors" data-id="element-5489">
                    <XIcon className="w-6 h-6" data-id="element-5490" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4" data-id="element-5491">
                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4" data-id="element-5492">
                  <div data-id="element-5493">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-5494">
                      <CalendarIcon className="w-4 h-4 inline mr-1" data-id="element-5495" />
                      Data Semnării *
                    </label>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-id="element-5496" />
                  </div>
                  <div data-id="element-5497">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-5498">
                      <ClockIcon className="w-4 h-4 inline mr-1" data-id="element-5499" />
                      Ora *
                    </label>
                    <input type="time" value={time} onChange={e => setTime(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent" data-id="element-5500" />
                  </div>
                </div>

                {/* Location */}
                <div data-id="element-5501">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-5502">
                    <MapPinIcon className="w-4 h-4 inline mr-1" data-id="element-5503" />
                    Locație Semnare
                  </label>
                  <select value={location} onChange={e => setLocation(e.target.value)} className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white" data-id="element-5504">
                    <option value="La birou" data-id="element-5505">La birou</option>
                    <option value="La proprietate" data-id="element-5506">La proprietate</option>
                    <option value="Online" data-id="element-5507">Online (video call)</option>
                    <option value="Altă locație" data-id="element-5508">Altă locație</option>
                  </select>
                </div>

                {/* Notes */}
                <div data-id="element-5509">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-5510">
                    Observații (opțional)
                  </label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="ex: Clientul va veni cu soția. Pregătește contractul în 2 exemplare..." className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none" data-id="element-5511" />
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-5512">
                  <p className="text-sm text-blue-900" data-id="element-5513">
                    <strong data-id="element-5514">Următorul pas:</strong> După programarea semnării,
                    vei putea marca contractul ca semnat și introduce valorile
                    de comisionare pentru facturare.
                  </p>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-slate-50 px-6 py-4 flex items-center justify-between border-t border-slate-200" data-id="element-5515">
                <button onClick={onClose} className="px-5 py-2.5 rounded-xl font-semibold text-slate-700 hover:bg-slate-200 transition-colors" data-id="element-5516">
                  Anulează
                </button>
                <button onClick={handleSubmit} className="px-6 py-2.5 rounded-xl font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:scale-105 transition-all" data-id="element-5517">
                  Programează Semnare
                </button>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}